import { ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import { Button, Link } from "@heroui/react";
import { ColumnDef } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

import { getAuditColumns } from "@/features/Audit/getAuditColumns";

import { download } from "@/lib/fetch";

import { Badge } from "@/components/Badge";
import type { BadgeProps } from "@/components/Badge";

export type A7OPCVMTimeSerieFile = {
  id: number;
  date: Date;
  error_message: string;
  status: string;
  key: string;
  strategy_id?: number;
  a7_opcvm_strategy_name?: string;
};

const tableName = "a7_opcvm_time_serie_files";

export const getTimeSerieFilesColumns = (
  t: TFunction,
): ColumnDef<A7OPCVMTimeSerieFile>[] => {
  const tTable = (key: string, defaultValue: string) =>
    t(`${tableName}.columns.${key}`, { ns: "tables", defaultValue });
  const tCommon = (key: string, defaultValue: string) =>
    t(`actions.${key}`, { ns: "common", defaultValue });

  return [
    {
      accessorKey: "id",
      header: tTable("id", "Id"),
    },
    {
      accessorKey: "date",
      header: tTable("date", "Date"),
    },
    {
      accessorKey: "a7_opcvm_strategy_name",
      header: tTable("a7_opcvm_strategy_name", "Fund"),
      cell: ({ row }) => {
        return (
          <Link
            href={toWorkspacePath(
              `/admin/a7-opcvm/a7_opcvm_strategies/${row.original.strategy_id}`,
            )}
            className="text-xs"
            underline="hover"
          >
            {row.getValue("a7_opcvm_strategy_name")}
          </Link>
        );
      },
    },
    {
      accessorKey: "status",
      header: tTable("status", "Status"),
      cell: ({ getValue }) => {
        return <Badge color={getValue() as BadgeProps["color"]} />;
      },
    },
    {
      accessorKey: "error_message",
      header: tTable("error_message", "Error message"),
      cell: ({ getValue }) => {
        const value = getValue() as string;
        const maxLength = 300;
        return (
          <div>
            {value
              ? value.length > maxLength
                ? `${value.substring(0, maxLength)}...`
                : value
              : ""}
          </div>
        );
      },
    },
    {
      accessorKey: "filename",
      header: tTable("filename", "Filename"),
    },
    {
      id: "actions",
      header: () => (
        <div className="text-center">{tTable("download", "Download")}</div>
      ),
      size: 10,
      cell: ({ row }) => {
        const href = `${
          import.meta.env.VITE_BACKEND_URL
        }/a7/a7_opcvm_time_serie_files/${row.getValue("id")}/download`;

        return (
          <div className="flex justify-center">
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onClick={() => download(href, row.getValue("filename"))}
            >
              <ArrowDownTrayIcon className="size-4 text-primary" />
            </Button>
          </div>
        );
      },
    },
    {
      id: "actions_2",
      cell: ({ row }) => {
        return (
          <div className="flex justify-end">
            <Link
              href={`${window.location.pathname}/${row.getValue("id")}`}
              className="text-xs"
              underline="hover"
            >
              {tCommon("edit", "Edit")}
            </Link>
          </div>
        );
      },
    },
  ];
};

export const getTimeSerieFilesAuditColumns = (t: TFunction) =>
  getAuditColumns<A7OPCVMTimeSerieFile>(getTimeSerieFilesColumns(t)).filter(
    (column) => {
      const key =
        (column as { accessorKey?: string; id?: string }).accessorKey ??
        column.id;
      return key !== "a7_opcvm_strategy_name";
    },
  );
